import type * as RPNInput from "react-phone-number-input";

// Map of common timezones to country codes
const timezoneToCountry: Record<string, string> = {
  // Europe
  "Europe/London": "GB",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "Europe/Warsaw": "PL",
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/Vienna": "AT",
  "Europe/Prague": "CZ",
  "Europe/Stockholm": "SE",
  "Europe/Copenhagen": "DK",
  "Europe/Oslo": "NO",
  "Europe/Helsinki": "FI",
  "Europe/Athens": "GR",
  "Europe/Lisbon": "PT",
  "Europe/Dublin": "IE",
  "Europe/Budapest": "HU",
  "Europe/Bucharest": "RO",
  "Europe/Sofia": "BG",
  "Europe/Zagreb": "HR",
  "Europe/Belgrade": "RS",
  "Europe/Zurich": "CH",
  "Europe/Moscow": "RU",
  "Europe/Kiev": "UA",
  "Europe/Istanbul": "TR",

  // Americas
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "America/Anchorage": "US",
  "Pacific/Honolulu": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Montreal": "CA",
  "America/Mexico_City": "MX",
  "America/Bogota": "CO",
  "America/Lima": "PE",
  "America/Santiago": "CL",
  "America/Buenos_Aires": "AR",
  "America/Sao_Paulo": "BR",
  "America/Caracas": "VE",

  // Asia
  "Asia/Tokyo": "JP",
  "Asia/Shanghai": "CN",
  "Asia/Hong_Kong": "HK",
  "Asia/Seoul": "KR",
  "Asia/Singapore": "SG",
  "Asia/Bangkok": "TH",
  "Asia/Jakarta": "ID",
  "Asia/Manila": "PH",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Kolkata": "IN",
  "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA",
  "Asia/Jerusalem": "IL",
  "Asia/Tehran": "IR",
  "Asia/Karachi": "PK",
  "Asia/Dhaka": "BD",
  "Asia/Colombo": "LK",

  // Oceania
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Perth": "AU",
  "Pacific/Auckland": "NZ",

  // Africa
  "Africa/Cairo": "EG",
  "Africa/Lagos": "NG",
  "Africa/Johannesburg": "ZA",
  "Africa/Nairobi": "KE",
  "Africa/Casablanca": "MA",
  "Africa/Tunis": "TN",
  "Africa/Algiers": "DZ",
};

// Map of locale codes to country codes
const localeToCountry: Record<string, string> = {
  "en-US": "US",
  "en-GB": "GB",
  "en-CA": "CA",
  "en-AU": "AU",
  "en-NZ": "NZ",
  "en-ZA": "ZA",
  "en-IE": "IE",
  "fr-FR": "FR",
  "fr-CA": "CA",
  "de-DE": "DE",
  "de-AT": "AT",
  "de-CH": "CH",
  "es-ES": "ES",
  "es-MX": "MX",
  "es-AR": "AR",
  "es-CO": "CO",
  "es-CL": "CL",
  "es-PE": "PE",
  "pt-BR": "BR",
  "pt-PT": "PT",
  "it-IT": "IT",
  "nl-NL": "NL",
  "nl-BE": "BE",
  "pl-PL": "PL",
  "ru-RU": "RU",
  "ja-JP": "JP",
  "ko-KR": "KR",
  "zh-CN": "CN",
  "zh-TW": "TW",
  "zh-HK": "HK",
  "ar-SA": "SA",
  "ar-AE": "AE",
  "ar-EG": "EG",
  "hi-IN": "IN",
  "th-TH": "TH",
  "vi-VN": "VN",
  "id-ID": "ID",
  "ms-MY": "MY",
  "tr-TR": "TR",
  "he-IL": "IL",
  "sv-SE": "SE",
  "da-DK": "DK",
  "no-NO": "NO",
  "fi-FI": "FI",
  "cs-CZ": "CZ",
  "hu-HU": "HU",
  "ro-RO": "RO",
  "bg-BG": "BG",
  "hr-HR": "HR",
  "sr-RS": "RS",
  "uk-UA": "UA",
  "el-GR": "GR",
};

export function detectUserCountry(): RPNInput.Country | undefined {
  try {
    // First try to get country from timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timezone && timezoneToCountry[timezone]) {
      return timezoneToCountry[timezone] as RPNInput.Country;
    }

    // Then try to get country from locale
    const locale = navigator.language;

    if (locale && localeToCountry[locale]) {
      return localeToCountry[locale] as RPNInput.Country;
    }

    // Try with just the language part of the locale
    const languageCode = locale.split("-")[0];
    const countryCode = locale.split("-")[1];

    if (countryCode && countryCode.length === 2) {
      return countryCode.toUpperCase() as RPNInput.Country;
    }

    // Default fallbacks based on language
    const languageFallbacks: Record<string, string> = {
      en: "US",
      es: "ES",
      fr: "FR",
      de: "DE",
      it: "IT",
      pt: "PT",
      pl: "PL",
      nl: "NL",
      ru: "RU",
      ja: "JP",
      ko: "KR",
      zh: "CN",
      ar: "SA",
      hi: "IN",
    };

    if (languageCode && languageFallbacks[languageCode]) {
      return languageFallbacks[languageCode] as RPNInput.Country;
    }

    // Default to US if nothing else works
    return "US" as RPNInput.Country;
  } catch (error) {
    console.error("Error detecting user country:", error);

    return undefined;
  }
}
